import { Container } from '@mui/material'
import { ColumnDef } from '@tanstack/react-table'
import { AppBox, AppTable, AppTypography, TextWithToolTip } from 'components'
import { centsToDollar } from 'globals/functions'
import { useMembershipHelper } from 'hooks'
import { FC, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { SubAccountAddedModel } from 'sdk'
import { PaymentMethodSliceSelector } from 'store/slices/payment-methods.slice'
interface Props {
  tempAddedUsers: SubAccountAddedModel[]
  onAmountCalculate: (amount: number) => void
}
export const AddSubAccountStepThreePurchase: FC<Props> = ({ tempAddedUsers, onAmountCalculate }) => {
  const { CurrentMembership } = useMembershipHelper()
  const { DefaultPaymentMethod } = useSelector(PaymentMethodSliceSelector)

  const isMonthlyPlan = useMemo(() => {
    return CurrentMembership()?.MembershipInterval === 'monthly'
  }, [CurrentMembership])
  const columns: ColumnDef<{ Item: string; No: string; Total: string }>[] = [
    {
      header: 'Item',
      accessorKey: 'Item',
    },
    {
      header: 'No. of Units',
      accessorKey: 'No',
    },
    {
      header: () => (
        <TextWithToolTip
          children={`Total/${isMonthlyPlan ? 'Mon' : 'Year'}`}
          tooltipText={'Pricing reflects base amount before being pro-rated or applying volume discounts.'}
        />
      ),
      accessorKey: 'Total',
    },
  ]

  const totalSeats = useMemo(() => {
    return tempAddedUsers.length
  }, [tempAddedUsers])

  const ammountwithOutDiscount = useMemo(() => {
    return totalSeats * (CurrentMembership()?.MembershipPrice ?? 0)
  }, [totalSeats, CurrentMembership()])

  const billingPeriod = useMemo(() => {
    return isMonthlyPlan ? 30 : 365
  }, [isMonthlyPlan])

  const billingDateDifference = useMemo(() => {
    const futureDate: Date = new Date(CurrentMembership()?.CurrentPeriodEnd!)
    futureDate.setMonth(futureDate.getMonth() - 1)

    // Check if the futureDate is valid
    if (isNaN(futureDate.getTime())) {
      throw new Error('Invalid future date')
    }

    // Current date
    const currentDate = new Date()

    // Calculate the difference in milliseconds
    const differenceInMilliseconds: number = currentDate.getTime() - futureDate.getTime()

    // Convert milliseconds to days
    return Math.ceil(differenceInMilliseconds / (1000 * 60 * 60 * 24))
  }, [CurrentMembership])

  const proRatedAmount = useMemo(() => {
    return (ammountwithOutDiscount / billingPeriod) * billingDateDifference
  }, [billingDateDifference, billingPeriod, ammountwithOutDiscount])

  const data = useMemo(() => {
    return {
      Item: `${CurrentMembership()?.MembershipTitle} - ${centsToDollar(CurrentMembership()?.MembershipPrice ?? 0)}(${CurrentMembership()?.MembershipBilled})`,
      No: `${totalSeats} User Seat(s)`,
      Total: centsToDollar(ammountwithOutDiscount),
    }
  }, [totalSeats, CurrentMembership(), ammountwithOutDiscount, tempAddedUsers])

  const finalAmount = useMemo(() => {
    return +ammountwithOutDiscount - +proRatedAmount
  }, [ammountwithOutDiscount, proRatedAmount])
  useEffect(() => {
    onAmountCalculate(Math.ceil(finalAmount))
  }, [finalAmount])

  return (
    <>
      <Container>
        <AppBox display={'flex'} flexDirection={'column'} justifyContent={'center'} gap={4}>
          <AppTypography fontSize={14} fontWeight={500} color={'grey.500'} component={'span'}>
            Please verify your order. Upon clicking the{' '}
            <AppTypography fontWeight={600} component={'span'}>
              Make Payment
            </AppTypography>{' '}
            button we’ll attempt to charge your payment method to complete the transaction.
          </AppTypography>
          <AppBox border={1} borderColor={'grey.300'} padding={4} borderRadius={6}>
            <AppTable data={[data ?? {}]} columns={columns} />
          </AppBox>
          <AppBox border={1} borderColor={'grey.300'} padding={4} borderRadius={6}>
            <AppBox display={'flex'} justifyContent={'space-between'}>
              <AppTypography fontSize={14} color={'grey.500'} fontWeight={500}>
                Payment Method
              </AppTypography>
              <AppBox display={'flex'} alignItems={'center'} gap={1}>
                <AppTypography fontSize={14} color={'primary.200'} fontWeight={600}>
                  {DefaultPaymentMethod?.CardType.toLocaleUpperCase() + ' '}
                  XXXX {DefaultPaymentMethod?.LastDigits}
                </AppTypography>
              </AppBox>
            </AppBox>
            <AppBox paddingTop={1}>
              <AppBox display={'flex'} justifyContent={'space-between'}>
                <TextWithToolTip tooltipText="Pro-rated amount is calculated by the number of days remaining in your plan’s current billing cycle.">
                  <AppTypography fontSize={14} color={'grey.500'} fontWeight={500}>
                    Pro Rated Amount
                  </AppTypography>
                </TextWithToolTip>
                <AppTypography fontSize={14} color={'grey.500'} fontWeight={500}>
                  {centsToDollar(finalAmount)}
                </AppTypography>
              </AppBox>
              <AppBox display={'flex'} justifyContent={'space-between'}>
                <AppTypography fontSize={14} color={'grey.500'} fontWeight={500}>
                  Discount Applied
                </AppTypography>
                <AppTypography fontSize={14} color={'grey.500'} fontWeight={500}>
                  {centsToDollar(0)}
                </AppTypography>
              </AppBox>
            </AppBox>
            <AppBox
              display={'flex'}
              justifyContent={'space-between'}
              borderTop={1}
              borderColor={'grey.300'}
              marginTop={2}
            >
              <AppTypography fontSize={14} fontWeight={'bold'}>
                Amount to be paid now
              </AppTypography>
              <AppTypography fontSize={14} fontWeight={'bold'}>
                {centsToDollar(finalAmount)}
              </AppTypography>
            </AppBox>
          </AppBox>
        </AppBox>
      </Container>
    </>
  )
}
